import React, { useState, useEffect, useCallback, useMemo } from 'react';
import apiService from '../../services/api';
import { formatPhone } from '../../utils/formatPhone';

/**
 * Everyone who claimed the email-and-text offer.
 *
 * A visitor gives an email and a US mobile, agrees to texts, and is sent one
 * $5 code by both. This is the record of that: which code each person holds,
 * whether it has been spent, whether the email and the text actually went
 * out, and whether they have since replied STOP.
 *
 * The delivery columns matter most. A code that never arrived is a customer
 * who was promised $5 and did not get it, and the reason the provider gave is
 * the only way to tell a typo from a broken integration.
 *
 * This is personal data — emails, phone numbers, consent records — so there is
 * deliberately no export here.
 */

interface Delivery {
  sent: boolean;
  at?: string;
  reason?: string;
  providerId?: string;
}

interface Lead {
  _id?: string;
  id?: string;
  email: string;
  phone: string;
  source?: string;
  smsConsent?: {
    consentedAt?: string;
    text?: string;
    ipAddress?: string;
    userAgent?: string;
  };
  smsOptedOutAt?: string;
  discountCode: string;
  discountAmount?: number;
  discountCurrency?: string;
  codeStatus: 'issued' | 'redeemed';
  redeemedAt?: string;
  redeemedSessionId?: string;
  emailDelivery?: Delivery;
  smsDelivery?: Delivery;
  createdAt: string;
  updatedAt?: string;
}

interface LeadsPage {
  leads: Lead[];
  cursor: string | null;
}

const PAGE_SIZE = 50;

const numberFormat = new Intl.NumberFormat('en-US');
const dateFormat = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const formatDate = (iso?: string) => {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : dateFormat.format(date);
};

const leadKey = (lead: Lead) => lead._id || lead.id || `${lead.email}-${lead.discountCode}`;

const DeliveryCell: React.FC<{ delivery?: Delivery }> = ({ delivery }) => {
  if (!delivery) {
    return <span className="text-ink-muted">Not recorded</span>;
  }

  if (delivery.sent) {
    return (
      <span className="text-ink-muted" title={delivery.at ? `Sent ${formatDate(delivery.at)}` : undefined}>
        Sent
      </span>
    );
  }

  return (
    <div title={delivery.reason}>
      <span className="text-brand-red">Not sent</span>
      {delivery.reason ? (
        <p className="mt-0.5 max-w-[16rem] break-words text-xs text-ink-muted">{delivery.reason}</p>
      ) : null}
    </div>
  );
};

// The api client throws Error(message) on a non-2xx, carrying the server's
// own words; a 200 with success:false is turned into the same thing here so
// both paths end up in one catch.
const fetchPage = async (pageCursor?: string): Promise<LeadsPage> => {
  const result = await apiService.getLeads({ limit: PAGE_SIZE, cursor: pageCursor });
  if (!result.success) {
    throw new Error(result.message || 'Could not load the customer list');
  }
  const data = (result as { data?: Partial<LeadsPage> }).data;
  return { leads: data?.leads ?? [], cursor: data?.cursor ?? null };
};

const errorMessage = (err: unknown) =>
  err instanceof Error && err.message ? err.message : 'Could not reach the server for the customer list';

const CustomerCRM: React.FC = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const page = await fetchPage();
      setLeads(page.leads);
      setCursor(page.cursor);
    } catch (err) {
      console.error('Customer CRM failed to load:', err);
      setError(errorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadMore = async () => {
    if (!cursor) return;
    setIsLoadingMore(true);
    setError(null);

    try {
      const page = await fetchPage(cursor);
      setLeads((current) => [...current, ...page.leads]);
      setCursor(page.cursor);
    } catch (err) {
      // The rows already loaded stay on screen; only the next page failed.
      console.error('Customer CRM failed to load more:', err);
      setError(errorMessage(err));
    } finally {
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(() => {
    const redeemed = leads.filter((l) => l.codeStatus === 'redeemed').length;
    const optedOut = leads.filter((l) => Boolean(l.smsOptedOutAt)).length;
    const failedEmail = leads.filter((l) => l.emailDelivery && !l.emailDelivery.sent).length;
    const failedSms = leads.filter((l) => l.smsDelivery && !l.smsDelivery.sent).length;
    return {
      total: leads.length,
      redeemed,
      redemptionRate: leads.length ? (redeemed / leads.length) * 100 : 0,
      optedOut,
      failedEmail,
      failedSms,
    };
  }, [leads]);

  const visibleLeads = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return leads;
    const digits = term.replace(/\D/g, '');
    return leads.filter((lead) => {
      if (lead.email?.toLowerCase().includes(term)) return true;
      if (lead.discountCode?.toLowerCase().includes(term)) return true;
      if (formatPhone(lead.phone).toLowerCase().includes(term)) return true;
      // "4155550134" or "415-555" should find +14155550134.
      return digits.length > 0 && (lead.phone || '').replace(/\D/g, '').includes(digits);
    });
  }, [leads, search]);

  if (isLoading) {
    return <p className="text-ink-muted">Loading customers…</p>;
  }

  if (error && leads.length === 0) {
    return (
      <div role="alert" className="rounded-lg border border-border-subtle bg-surface p-6">
        <p className="text-brand-red mb-4">{error}</p>
        <button type="button" onClick={load} className="btn-secondary">Try again</button>
      </div>
    );
  }

  const tiles = [
    { label: 'Leads', value: numberFormat.format(summary.total), hint: 'Claimed the $5 code' },
    {
      label: 'Codes redeemed',
      value: numberFormat.format(summary.redeemed),
      hint: `${summary.redemptionRate.toFixed(1)}% redemption rate`,
    },
    { label: 'Texts opted out', value: numberFormat.format(summary.optedOut), hint: 'Replied STOP' },
    {
      label: 'Deliveries failed',
      value: numberFormat.format(summary.failedEmail + summary.failedSms),
      hint: `${numberFormat.format(summary.failedEmail)} email, ${numberFormat.format(summary.failedSms)} text`,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-ink">Customer CRM</h2>
          <p className="text-ink-muted">Everyone who claimed the email-and-text offer, newest first</p>
        </div>
        <button type="button" onClick={load} className="btn-secondary">Refresh</button>
      </div>

      {error ? (
        <div role="alert" className="rounded-lg border border-border-subtle bg-surface px-4 py-3">
          <p className="text-brand-red">{error}</p>
        </div>
      ) : null}

      <div>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {tiles.map((tile) => (
            <div
              key={tile.label}
              data-testid="crm-summary-tile"
              className="rounded-lg border border-border-subtle bg-surface px-4 py-3"
            >
              <p className="text-xs uppercase tracking-wide text-ink-muted">{tile.label}</p>
              <p className="text-2xl font-semibold text-ink">{tile.value}</p>
              <p className="text-xs text-ink-muted">{tile.hint}</p>
            </div>
          ))}
        </div>
        {cursor ? (
          <p className="mt-2 text-xs text-ink-muted">
            These figures cover the {numberFormat.format(leads.length)} leads loaded so far. Load more to include the rest.
          </p>
        ) : null}
      </div>

      {leads.length === 0 ? (
        <div className="rounded-lg border border-border-subtle bg-surface p-8 text-center">
          <p className="text-ink-muted">
            Nobody has claimed the offer yet. Leads appear here once a visitor gives an email and mobile number for the $5 code.
          </p>
        </div>
      ) : (
        <>
          <div>
            <label htmlFor="crm-search" className="sr-only">Search leads</label>
            <input
              id="crm-search"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by email, phone, or code"
              className="w-full max-w-sm rounded-lg border border-border-default bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue"
            />
          </div>

          <div className="overflow-x-auto rounded-lg border border-border-subtle bg-surface">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border-subtle text-xs uppercase tracking-wide text-ink-muted">
                <tr>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Email delivery</th>
                  <th className="px-4 py-3">SMS delivery</th>
                  <th className="px-4 py-3">SMS consent</th>
                  <th className="px-4 py-3">Source</th>
                </tr>
              </thead>
              <tbody>
                {visibleLeads.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-6 text-center text-ink-muted">
                      No loaded leads match “{search.trim()}”.
                    </td>
                  </tr>
                ) : (
                  visibleLeads.map((lead) => (
                    <tr key={leadKey(lead)} className="border-b border-border-subtle align-top last:border-0">
                      <td className="whitespace-nowrap px-4 py-3 text-ink-muted">{formatDate(lead.createdAt)}</td>
                      <td className="px-4 py-3 text-ink">{lead.email}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-ink">{formatPhone(lead.phone)}</td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <span className="font-mono text-ink">{lead.discountCode}</span>
                        <p className="text-xs text-ink-muted">
                          {lead.codeStatus === 'redeemed'
                            ? `Redeemed ${formatDate(lead.redeemedAt)}`
                            : 'Issued'}
                        </p>
                      </td>
                      <td className="px-4 py-3"><DeliveryCell delivery={lead.emailDelivery} /></td>
                      <td className="px-4 py-3"><DeliveryCell delivery={lead.smsDelivery} /></td>
                      <td className="whitespace-nowrap px-4 py-3">
                        {lead.smsOptedOutAt ? (
                          <span className="text-brand-red">Opted out {formatDate(lead.smsOptedOutAt)}</span>
                        ) : lead.smsConsent?.consentedAt ? (
                          <span className="text-ink-muted">Consented {formatDate(lead.smsConsent.consentedAt)}</span>
                        ) : (
                          <span className="text-ink-muted">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-ink-muted">{lead.source || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {cursor ? (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={loadMore}
                disabled={isLoadingMore}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default CustomerCRM;
